import React, { useState, ChangeEvent } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  Alert,
  Stack,
} from "@mui/material";
import { FormatAlignLeft, Clear, CheckCircle } from "@mui/icons-material";

interface JsonEditorStorageProps {
  textareaRows?: number;
  textareaHeight?: string;
  containerMaxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  setData?: any;
  name?: string;
}

export default function JsonEditorStorage({
  textareaRows = 16,
  textareaHeight,
  containerMaxWidth = "lg",
  setData,
  name
}: JsonEditorStorageProps = {}): JSX.Element {
  const [jsonText, setJsonText] = useState<string>(JSON.stringify({}, null, 2));

  const [storedJson, setStoredJson] = useState<unknown>({});

  const [error, setError] = useState<string>("");

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    const text = e.target.value;
    setJsonText(text);
    
    try {
      const parsed = JSON.parse(text);
      setStoredJson(parsed);
      setData(parsed); // ✅ FIXED: Pass parsed object, not string
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid JSON");
      // Don't update parent state if JSON is invalid
    }
  };

  const formatJson = (): void => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setData(parsed); // ✅ FIXED: Pass parsed object, not string
      setError("");
    } catch (err) {
      setError("Cannot format invalid JSON");
    }
  };

  const clearEditor = (): void => {
    setJsonText("{}");
    setData({}); // ✅ FIXED: Pass empty object, not string "{}"
    setStoredJson({});
    setError("");
  };

  return (
    <Box
      sx={{marginTop:"10px"}}
    >
      <Container maxWidth={containerMaxWidth}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              {name}
            </Typography>
            <Stack direction="row" spacing={1}>
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>Error:</strong> {error}
                </Alert>
              )}

              {!error && (
                <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 2 }}>
                  Valid JSON
                </Alert>
              )}
              <Button
                variant="contained"
                startIcon={<FormatAlignLeft />}
                onClick={formatJson}
              >
                Format JSON
              </Button>
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={clearEditor}
                color="inherit"
              >
                Clear
              </Button>
            </Stack>
          </Stack>

          <TextField
            value={jsonText}
            onChange={handleTextChange}
            multiline
            rows={textareaRows}
            fullWidth
            placeholder="Paste your JSON here..."
            variant="outlined"
            InputProps={{
              sx: {
                fontFamily: "monospace",
                fontSize: "0.875rem",
                ...(textareaHeight && { height: textareaHeight }),
              },
            }}
            sx={{
              ...(textareaHeight && {
                "& .MuiInputBase-root": {
                  height: textareaHeight,
                },
              }),
            }}
          />
        </Paper>
      </Container>
    </Box>
  );
}